import {
  DragDropContext,
  type DragDropState,
  DragSource,
  DropTarget,
} from '@wandb/weave/common/containers/DragDropContainer';
import {isFirefox} from '@wandb/weave/common/util/browser';
import {clsx} from 'clsx';
import {produce} from 'immer';
import * as _ from 'lodash';
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {Resizable} from 'react-resizable';

import {useSelector} from '../../state/hooks';
import * as ViewHooks from '../../state/views/hooks';
import type * as PanelTypes from '../../state/views/panel/types';
import * as PanelBankConfigActions from '../../state/views/panelBankConfig/actions';
import type {Ref as PanelBankConfigRef} from '../../state/views/panelBankConfig/types';
import * as PanelBankSectionConfigActions from '../../state/views/panelBankSectionConfig/actions';
import type {Ref as PanelBankSectionRef} from '../../state/views/panelBankSectionConfig/types';
import {skipTransition} from '../../util/animations';
import {isPanel} from '../../util/panelbank';
import {isMobile} from '../../util/panelbankFlow';
import {
  compact,
  DROP_PREVIEW_ID,
  getLayoutItem,
  getNewGridItemLayout,
  GRID_COLUMN_COUNT,
  GRID_ITEM_DEFAULT_HEIGHT,
  GRID_ITEM_DEFAULT_WIDTH,
  type GridLayout,
  type GridLayoutItem,
  REPORT_GRID_CONTAINER_PADDING,
  REPORT_GRID_ITEM_MARGIN,
  REPORT_GRID_ROW_HEIGHT,
} from '../../util/panelbankGrid';
import {type OrganizedSettings} from '../PanelBank/types';
import {ReportDiscussionContext} from '../ReportDiscussion/ReportDiscussionContext';
import {GridLayoutDropPreview} from './gridLayout/GridLayoutDropPreview';
import {
  convertToGridHeight,
  useResizingSize,
} from './gridLayout/useResizingSize';
import {useStyleFromGridLayout} from './gridLayout/useStyleFromLayout';
import {convertToGridCoords, getColumnWidth} from './gridLayout/utils';
import {getGridBottom, getIsSingleMarkdownPanel} from './gridLayoutUtils';
import {ReportPanelContainer} from './ReportPanel/ReportPanel';

export type ReportGridLayoutProps = {
  inheritedSettings: OrganizedSettings;
  panelBankConfigRef: PanelBankConfigRef;
  panelBankSectionConfigRef: PanelBankSectionRef;
  panelBankWidth: number;
  readOnly?: boolean; // whether user can edit section contents (e.g. reorder or edit panels)
  onClickGrid?(): void;
};

export const ReportGridLayout = (props: ReportGridLayoutProps) => {
  const showGridDots = !props.readOnly;

  const view = useSelector(
    state => state.views.views[props.panelBankSectionConfigRef.viewID]
  );

  const panelRefs = ViewHooks.usePart(
    props.panelBankSectionConfigRef
  ).panelRefs;
  // Note: not using useWholeMapped below because, with it, section.panels is stale if you delete a panel
  const section = ViewHooks.usePart(props.panelBankSectionConfigRef);
  const panels = ViewHooks.useParts(section.panelRefs);
  const serverGridLayout: GridLayout = [];
  panels.forEach((p, i) => {
    serverGridLayout.push({
      ...(p.layout || getNewGridItemLayout(serverGridLayout)), // Add layout to panels that don't have it
      id: panelRefs[i].id,
    });
  });
  const setGridLayout = ViewHooks.useViewAction(
    props.panelBankSectionConfigRef,
    PanelBankSectionConfigActions.setGridLayout
  );

  const movePanel = ViewHooks.useViewAction(
    props.panelBankConfigRef,
    PanelBankConfigActions.movePanel
  );

  const [contentHeightByPanelRefID, setContentHeightByPanelRefID] = useState<{
    [id: string]: number;
  }>({});

  const {dragRef, dropRef, dragData, dragging} = useContext(DragDropContext);
  const [resizingRefId, setResizingRefId] = React.useState<string | null>(null);
  const [resizingPixelWidth, setResizingPixelWidth] = useState<number | null>(
    null
  );
  const [resizingPixelHeight, setResizingPixelHeight] = useState<number | null>(
    null
  );

  const columnWidth = useMemo(
    () =>
      getColumnWidth({
        gridContainerPaddingX: REPORT_GRID_CONTAINER_PADDING[0],
        gridItemMarginX: REPORT_GRID_ITEM_MARGIN[0],
        panelBankWidth: props.panelBankWidth,
      }),
    [props.panelBankWidth]
  );

  const getStyleFromLayout = useStyleFromGridLayout({
    columnWidth,
    gridContainerPadding: [...REPORT_GRID_CONTAINER_PADDING],
    gridItemMargin: [...REPORT_GRID_ITEM_MARGIN],
    gridRowHeight: REPORT_GRID_ROW_HEIGHT,
  });

  const getResizingSize = useResizingSize({
    columnWidth,
    gridItemMargin: [...REPORT_GRID_ITEM_MARGIN],
    gridRowHeight: REPORT_GRID_ROW_HEIGHT,
    resizingPixelHeight,
    resizingPixelWidth,
  });

  const getLayoutHeight = useCallback(
    (layout: GridLayout) => {
      const bottomY = getGridBottom(showGridDots, layout);
      return Math.max(
        bottomY * REPORT_GRID_ROW_HEIGHT +
          (bottomY - 1) * REPORT_GRID_ITEM_MARGIN[1] +
          REPORT_GRID_CONTAINER_PADDING[1] * 2,
        0
      );
    },
    [showGridDots]
  );

  const forceResize = () => {
    setTimeout(() => window.dispatchEvent(new Event('resize')));
  };

  const getPanelSizeFromDragData = (data: any) => {
    return {
      w: data?.layout?.w || data?.size?.w || GRID_ITEM_DEFAULT_WIDTH,
      h: data?.layout?.h || data?.size?.h || GRID_ITEM_DEFAULT_HEIGHT,
    };
  };

  const dragPivotStartXRef = useRef(0);
  // const [dragPivotStartX, setDragPivotStartX] = useState<number>(0);
  /**
   * |0--25--50--75--100|
   * |---[    x   ]-----|
   * |------------------|
   *
   * 50 -> original mouse down
   * 25 left edge of panel
   * 50-25=25
   *
   * So the logic is (current - (original - left side))
   *
   * When on the initial point of contact (50 - (50-25)) leaves us with 25, the left corner of the panel.
   * Now lets say we move 25px to the right: (75 - (50-25)) which is now 50, 25 px to the right as expected.
   */
  const updateDragoverMouseCoords = (
    ctx: DragDropState,
    e: React.DragEvent<Element>
  ) => {
    const dragPivotStartX = dragPivotStartXRef.current;
    const sectionBounds = e.currentTarget.getBoundingClientRect();
    const panelLeftEdge = (dragData?.layout.x || 1) * columnWidth;
    const offsetFromLeftOfMouse = dragPivotStartX - panelLeftEdge;

    const coords = convertToGridCoords({
      columnWidth,
      gridItemMargin: [...REPORT_GRID_ITEM_MARGIN],
      gridRowHeight: REPORT_GRID_ROW_HEIGHT,
      xPx: e.clientX - offsetFromLeftOfMouse,
      yPx: e.clientY - sectionBounds.top - 60,
    });
    if (!_.isEqual(coords, dragData && dragData.dragoverMouseCoords)) {
      ctx.setDragData({
        ...dragData,
        dragoverMouseCoords: coords,
      });
    }
  };

  const serverGridLayoutWithoutDragging = produce(serverGridLayout, draft => {
    if (
      dragRef != null &&
      dragging &&
      dragData != null &&
      dragData.dragoverMouseCoords != null
    ) {
      const draggingItemIndex = _.findIndex(draft, {id: dragRef.id});
      if (draggingItemIndex !== -1) {
        draft.splice(draggingItemIndex, 1);
      }
    }
  });

  const draggingOverSection =
    dragData != null && _.isEqual(dropRef, props.panelBankSectionConfigRef);

  let derivedGridLayout = produce(serverGridLayoutWithoutDragging, draft => {
    if (resizingRefId != null) {
      const resizingItemIndex = _.findIndex(draft, {id: resizingRefId});
      if (resizingItemIndex !== -1) {
        const resizingItem = draft[resizingItemIndex];
        const resizingSize = getResizingSize(resizingItem?.x);
        if (resizingSize != null) {
          draft[resizingItemIndex].w = resizingSize.w;
          draft[resizingItemIndex].h = resizingSize.h;
        }
      }
    }
    if (
      dragRef != null &&
      dragging &&
      draggingOverSection &&
      dragData != null &&
      dragData.dragoverMouseCoords != null
    ) {
      const size = getPanelSizeFromDragData(dragData);
      draft.push({
        x: Math.min(dragData.dragoverMouseCoords.x, GRID_COLUMN_COUNT - size.w),
        y: dragData.dragoverMouseCoords.y,
        ...size,
        id: DROP_PREVIEW_ID,
      });
    }
  });

  // Stack vertically on mobile
  if (isMobile() && props.readOnly) {
    derivedGridLayout = produce(derivedGridLayout, draft => {
      draft.forEach(layout => {
        layout.w = GRID_COLUMN_COUNT;
        layout.x = 0;
      });
    });
  }

  derivedGridLayout = compact(
    derivedGridLayout,
    GRID_COLUMN_COUNT,
    !view?.saving ? view?.updatedAt : undefined
  );

  // This is sum hilarious hax to automatically resize a markdown panel to its content's height
  // It's only active when props.readOnly === true which means a report has been published
  // Original PR: https://github.com/wandb/core/pull/4765
  derivedGridLayout.forEach(l => {
    const contentHeight = contentHeightByPanelRefID[l.id];
    if (contentHeight == null) {
      return;
    }

    // Abort if there are other panels in the same row
    if (
      derivedGridLayout.find(
        otherL => l !== otherL && heightsIntersect(l, otherL)
      ) != null
    ) {
      return;
    }

    const oldH = l.h;
    // These magic numbers correspond to the padding/border of the elements between here and the content
    // Use Math.max to preserve the user-defined height as a minimum,
    // only expanding the panel if content is taller
    l.h = Math.max(
      l.h,
      convertToGridHeight({
        gridItemMargin: [...REPORT_GRID_ITEM_MARGIN],
        gridRowHeight: REPORT_GRID_ROW_HEIGHT,
        h: contentHeight + 32 + 6,
      })
    );

    // Shift panels below this one accordingly
    derivedGridLayout.forEach(otherL => {
      if (l !== otherL && otherL.y > l.y) {
        otherL.y += l.h - oldH;
      }
    });
  });

  const lowest = getGridBottom(showGridDots, derivedGridLayout);
  const dots = [];
  if (showGridDots) {
    for (let i = 0; i <= lowest / 2; i++) {
      for (let j = 0; j < 13; j++) {
        dots.push([
          (props.panelBankWidth / 12) * j,
          i * (REPORT_GRID_ROW_HEIGHT - 1) * 2,
        ]);
      }
    }
  }

  // TODO: Filter histograms if multi-run?
  const activePanelRefs = ViewHooks.usePart(
    props.panelBankSectionConfigRef
  ).panelRefs;

  const empty = activePanelRefs.length === 0;

  const {highlightIds} = useContext(ReportDiscussionContext);

  const activePanelIds = ViewHooks.useParts(
    activePanelRefs as PanelTypes.Ref[]
  ).map(p => p.__id__);

  const [clickingGrid, setClickingGrid] = useState(false);
  useEffect(() => {
    function onMouseUp() {
      setClickingGrid(false);
    }
    window.addEventListener('mouseup', onMouseUp);
    return () => window.removeEventListener('mouseup', onMouseUp);
  }, []);

  const dropPreviewLayout = useMemo(() => {
    return getLayoutItem(derivedGridLayout, DROP_PREVIEW_ID);
  }, [derivedGridLayout]);

  return (
    <div
      className={clsx('grid-section', {
        'one-markdown': getIsSingleMarkdownPanel({
          readOnly: props.readOnly,
          panels,
        }),
        'resizing-panel': resizingRefId != null,
        empty,
      })}>
      <DropTarget
        partRef={props.panelBankSectionConfigRef}
        style={{
          position: 'relative',
          height: getLayoutHeight(derivedGridLayout),
        }}
        onMouseDown={e => {
          dragPivotStartXRef.current = e.clientX;
          if (e.target === e.currentTarget) {
            setClickingGrid(true);
          }
        }}
        onMouseUp={e => {
          dragPivotStartXRef.current = 0;
          if (clickingGrid && e.target === e.currentTarget) {
            props.onClickGrid?.();
          }
        }}
        onDragEnter={(ctx, e) => {
          updateDragoverMouseCoords(ctx, e);
        }}
        onDragOver={updateDragoverMouseCoords}
        onDrop={() => {
          if (!dragData || !dragRef) {
            return;
          }
          if (
            !_.isEqual(dragData.fromSectionRef, props.panelBankSectionConfigRef)
          ) {
            movePanel(
              {
                panelRef: dragRef as PanelTypes.Ref,
                sectionRef: dragData.fromSectionRef,
              },
              {
                panelRef: null,
                sectionRef: props.panelBankSectionConfigRef,
                insertDir: 'before',
              }
            );
          }
          const dropPreviewItem = getLayoutItem(
            derivedGridLayout,
            DROP_PREVIEW_ID
          );
          if (dropPreviewItem) {
            dropPreviewItem.id = dragRef.id;
          }
          setGridLayout(derivedGridLayout);
        }}>
        <GridLayoutDropPreview
          style={getStyleFromLayout({
            layout: dropPreviewLayout,
            useTransform: false,
          })}
        />
        {dots.map((dot, i) => (
          <div
            className="grid-dot"
            // oxlint-disable-next-line react/no-array-index-key -- https://oxc.rs/docs/guide/usage/linter/rules/react/no-array-index-key.html
            key={`dot-${i}`}
            style={{left: dot[0], top: dot[1]}}
          />
        ))}
        {activePanelRefs.map((panelRef, i) => {
          const panelId = activePanelIds[i];

          let layout = getLayoutItem(derivedGridLayout, panelRef.id);
          if (layout == null) {
            // If it's not in the layout, it's probably being dragged,
            // in which case we still need to render it for its drag events to fire,
            // but we want it to be invisible.
            layout = {w: 0, h: 0, x: 0, y: 0, id: ''};
          }
          const selectedForDrag =
            isPanel(dragRef) && _.isEqual(dragRef, panelRef);
          const panelLayoutStyle = getStyleFromLayout({
            layout,
            useTransform: !isFirefox && (!selectedForDrag || dragging),
          });

          // This is sum hilarious hax to automatically resize a markdown panel to its content's height
          // It's only active when props.readOnly === true which means a report has been published
          // Original PR: https://github.com/wandb/core/pull/4765
          const onContentHeightChanged = props.readOnly
            ? (h: number) =>
                setContentHeightByPanelRefID(old => ({
                  ...old,
                  [panelRef.id]: h,
                }))
            : undefined;

          const resizingSize = getResizingSize(layout?.x);

          const isResizing =
            resizingRefId != null &&
            resizingSize != null &&
            resizingRefId === panelRef.id;

          const panelPixelSize = isResizing
            ? {w: resizingPixelWidth, h: resizingPixelHeight}
            : {w: panelLayoutStyle.width, h: panelLayoutStyle.height};

          return (
            <Resizable
              className={clsx('panel-bank__panel', `col-${layout.w}`, {
                [`panel-bank__panel-id__${panelId}`]: panelId != null,
                resizing: isResizing,
                dragging: selectedForDrag && dragging,
                'panel-highlight':
                  panelId != null && highlightIds.includes(panelId),
              })}
              key={panelRef.id}
              width={panelPixelSize?.w ?? 0}
              height={panelPixelSize?.h ?? 0}
              minConstraints={[columnWidth, REPORT_GRID_ROW_HEIGHT]}
              onResize={(e, data) => {
                setResizingPixelWidth(data.size.width);
                setResizingPixelHeight(data.size.height);
                forceResize();
              }}
              onResizeStart={() => {
                setResizingRefId(panelRef.id);
              }}
              onResizeStop={() => {
                // Must be in a timeout otherwise we hit an issue related to a race
                // condition inside of Slate
                setTimeout(() => {
                  setResizingRefId(null);
                  setResizingPixelHeight(null);
                  setResizingPixelWidth(null);
                  setGridLayout(derivedGridLayout);
                }, 1);
              }}>
              <DragSource
                style={panelLayoutStyle}
                partRef={panelRef}
                onMouseUp={e => {
                  skipTransition(e.currentTarget, 50);
                }}
                data={{
                  fromSectionRef: props.panelBankSectionConfigRef,
                  layout,
                }}>
                <ReportPanelContainer
                  inheritedSettings={props.inheritedSettings}
                  isReadOnly={props.readOnly}
                  onContentHeightChange={onContentHeightChanged}
                  panelBankSectionConfigRef={props.panelBankSectionConfigRef}
                  panelRef={panelRef}
                />
              </DragSource>
            </Resizable>
          );
        })}
      </DropTarget>
    </div>
  );
};

function heightsIntersect(l1: GridLayoutItem, l2: GridLayoutItem) {
  const higher = l2.y < l1.y ? l2 : l1;
  const lower = higher === l1 ? l2 : l1;
  return lower.y < higher.y + higher.h;
}
